<script setup lang="ts">
import { computed, inject } from 'vue'
import SiteEditableText from '../../SiteEditableText.vue'
import { websiteEditableContextKey } from '../../editable-context'
import { extractGoogleMapsEmbedUrl } from '../../google-maps'
import type { MapBlock } from '../../types'

const props = defineProps<{ block: MapBlock }>()
const editableContext = inject(websiteEditableContextKey, null)
const isEditable = computed(() => editableContext?.editable.value ?? false)

const mapUrl = computed(() => {
  const suppliedEmbed = props.block.content.embedUrl?.trim()

  if (suppliedEmbed) {
    try {
      const url = new URL(extractGoogleMapsEmbedUrl(suppliedEmbed))
      const isGoogleHost = url.hostname === 'google.com'
        || url.hostname === 'www.google.com'
        || url.hostname === 'maps.google.com'

      if (url.protocol === 'https:' && isGoogleHost && url.pathname.startsWith('/maps/embed')) {
        return url.toString()
      }
    } catch {
      // Fall back to the address-based Google Maps embed.
    }
  }

  return `https://www.google.com/maps?q=${encodeURIComponent(props.block.content.address)}&output=embed`
})
</script>

<template>
  <section :id="block.settings.anchorId" class="site:bg-(--site-color-background) site:py-16 site:@min-[40rem]:py-24">
    <div class="site-site-container site:grid site:items-stretch site:gap-8 site:@min-[64rem]:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] site:@min-[64rem]:gap-12">
      <div class="site:flex site:flex-col site:justify-center site:bg-(--site-color-surface) site:p-7 site:@min-[40rem]:p-10" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <SiteEditableText v-if="block.content.eyebrow" v-model="block.content.eyebrow" as="p" class="site:text-sm site:font-semibold site:text-(--site-color-primary)" />
        <SiteEditableText v-model="block.content.heading" as="h2" class="site-heading site:mt-3 site:text-3xl site:font-bold site:tracking-tight site:@min-[40rem]:text-5xl" />
        <SiteEditableText v-model="block.content.description" as="p" class="site:mt-5 site:text-lg site:leading-8 site:text-(--site-color-muted-text)" />
        <div class="site:mt-8 site:border-t site:border-(--site-color-border) site:pt-6">
          <p class="site:text-xs site:font-bold site:tracking-wider site:text-(--site-color-muted-text) site:uppercase">Location</p>
          <SiteEditableText v-model="block.content.address" as="p" class="site-heading site:mt-2 site:text-lg site:font-bold" />
        </div>
      </div>

      <div class="site:relative site:min-h-80 site:overflow-hidden site:bg-(--site-color-surface) site:shadow-(--site-shadow-md) site:@min-[64rem]:min-h-[30rem]" :style="{ borderRadius: 'var(--site-radius-lg)' }">
        <iframe
          :src="mapUrl"
          :title="`Map showing ${block.content.address}`"
          class="site:absolute site:inset-0 site:h-full site:w-full site:border-0"
          :class="isEditable ? 'site:pointer-events-none' : undefined"
          loading="lazy"
          allowfullscreen
          referrerpolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  </section>
</template>
